import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, FileText, Sparkles, Gift, Target, Users, MessagesSquare, Info, Newspaper } from 'lucide-react';
import { useClubBranding } from '../context/ClubBrandingContext';

/**
 * FoundingBanner (formerly BetaNoticeBanner) — merged front-page banner.
 *
 * Combines what used to be two banners:
 *   1. dark-slate "Compact Welcome Banner" (club identity + Features/About/News)
 *   2. amber "Founding Members Welcome" (Charter upsell + collapsible sections)
 *
 * Now a single wide banner with:
 *   - club branding logo + tagline as the primary identifier
 *   - Founding-Member Charter upsell in the sub-line
 *   - Features / About / News quick links (visible on mobile AND desktop)
 *   - Same collapsible "Learn More / Our Purpose / This is Your Club / Create Your Own Community" sections
 *
 * Background color comes from the club's admin-configurable `banner_color`
 * (falls back to forest green for AADC).
 */
export default function BetaNoticeBanner() {
  const branding = useClubBranding();
  const navigate = useNavigate();
  const [betaExpanded, setBetaExpanded] = useState(false);
  const [missionExpanded, setMissionExpanded] = useState(false);
  const [ownershipExpanded, setOwnershipExpanded] = useState(false);
  const [communitiesExpanded, setCommunitiesExpanded] = useState(false);

  const bannerBg = branding.bannerColor || '#fffbeb'; // amber-50 default

  return (
    <div className="border-b border-amber-200" data-testid="beta-notice-banner" style={{ backgroundColor: bannerBg }}>
      <div className="container mx-auto px-4 py-3 text-amber-900">
        {/* Identity + Charter upsell */}
        <div className="flex items-center gap-3">
          {branding.logoUrlSmall || branding.logoUrl ? (
            <img
              src={branding.logoUrlSmall || branding.logoUrl}
              alt={branding.logoAlt || 'Club mascot'}
              className="w-11 h-11 sm:w-12 sm:h-12 rounded-full object-cover ring-2 ring-amber-300 shrink-0"
              data-testid="banner-logo"
            />
          ) : (
            <Sparkles className="w-6 h-6 text-amber-600 shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm sm:text-base font-semibold leading-tight" data-testid="banner-tagline">
              Member-governed online community for dog people &amp; dog clubs
            </p>
            <p className="text-[11px] sm:text-xs text-amber-800 mt-0.5">
              <span className="font-semibold">Founding Members</span>
              <span className="hidden sm:inline"> — Join now to help shape the Club and lock in early-member pricing.</span>
              <span className="sm:hidden"> — Lock in early-member pricing!</span>
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Link
              to="/membership"
              className="hidden sm:inline-flex items-center gap-1 text-xs bg-amber-600 hover:bg-amber-700 text-white px-2.5 py-1 rounded font-medium transition-colors"
              data-testid="beta-membership-link"
            >
              <Gift className="w-3 h-3" /> Charter Discount
            </Link>
            <button
              onClick={() => setBetaExpanded(!betaExpanded)}
              className="flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 font-medium transition-colors px-2 py-1 rounded hover:bg-amber-100"
              data-testid="beta-notice-toggle"
            >
              {betaExpanded ? 'Less' : 'Learn More'}
              {betaExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </div>
        </div>

        {/* Quick links — visible on both mobile and desktop (icons on mobile, icons+labels on sm+) */}
        <div className="mt-2 flex items-center gap-1 sm:gap-2 flex-wrap">
          <button
            onClick={() => navigate('/about/future')}
            className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-amber-800 hover:text-amber-900 bg-amber-100/70 hover:bg-amber-100 px-2 py-1 rounded-full transition-colors"
            data-testid="banner-features-link"
            title="Features"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Features</span>
          </button>
          <button
            onClick={() => navigate('/about')}
            className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-amber-800 hover:text-amber-900 bg-amber-100/70 hover:bg-amber-100 px-2 py-1 rounded-full transition-colors"
            data-testid="banner-about-link"
            title="About"
          >
            <Info className="w-3.5 h-3.5" />
            <span>About</span>
          </button>
          <button
            onClick={() => navigate('/club-blog')}
            className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-amber-800 hover:text-amber-900 bg-amber-100/70 hover:bg-amber-100 px-2 py-1 rounded-full transition-colors"
            data-testid="banner-news-link"
            title="News"
          >
            <Newspaper className="w-3.5 h-3.5" />
            <span>News</span>
          </button>
          {/* Mobile-only Charter shortcut so mobile users don't miss it */}
          <Link
            to="/membership"
            className="sm:hidden inline-flex items-center gap-1.5 text-[11px] text-white bg-amber-600 hover:bg-amber-700 px-2 py-1 rounded-full font-medium transition-colors ml-auto"
            data-testid="banner-charter-mobile"
          >
            <Gift className="w-3.5 h-3.5" /> Charter Pricing
          </Link>
        </div>

        {/* Founding Member Details (collapsible) */}
        {betaExpanded && (
          <div className="mt-2 pt-2 border-t border-amber-200" data-testid="beta-notice-details">
            <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800 font-medium flex items-center gap-2">
                <Gift className="w-4 h-4" />
                Founding Member Special: lock in early-member pricing
              </p>
              <p className="text-xs text-green-700 mt-1">
                Join now during our Founding Member preview and keep Charter pricing for the life of your subscription.
                <Link to="/membership" className="underline ml-1 font-medium">View membership options →</Link>
              </p>
            </div>

            {/* Founding Member role choice */}
            <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 font-medium">Choose Your Role</p>
              <p className="text-xs text-blue-700 mt-1">
                <span className="font-semibold">Founding Contributor:</span> Help shape the platform — test new features, report bugs,
                and vote on what we build next. Contributors get recognition, early access, and a real voice in the roadmap.
              </p>
              <p className="text-xs text-blue-700 mt-1">
                <span className="font-semibold">Or just join:</span> Not interested in testing? No problem —
                register as a regular Founding Member and enjoy the platform. You still lock in the Charter discount.
              </p>
              <Link to="/beta-agreement" className="inline-flex items-center gap-1 text-xs text-blue-600 font-medium hover:text-blue-800 mt-1">
                Learn about Founding Member benefits →
              </Link>
            </div>

            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-amber-800">
              <li className="flex items-start gap-1.5">
                <span className="text-amber-500 mt-0.5">✓</span>
                Open to everyone — no invite or access code needed
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-amber-500 mt-0.5">✓</span>
                Your feedback directly shapes what we build
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-amber-500 mt-0.5">*</span>
                Features may be added, changed, or removed as we improve
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-amber-500 mt-0.5">*</span>
                Pricing and policies may adjust during the Founding Member preview
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-amber-500 mt-0.5">✓</span>
                Your data is never sold to third parties
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-amber-500 mt-0.5">✓</span>
                We're committed to preserving your pet profiles and records
              </li>
            </ul>

            <div className="mt-2 flex items-center justify-between">
              <Link
                to="/beta-agreement"
                className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium hover:text-amber-900"
              >
                <FileText className="w-3 h-3" /> Read Full Founding Member Agreement
              </Link>
              <Link
                to="/membership"
                className="sm:hidden inline-flex items-center gap-1 text-xs bg-amber-600 text-white px-2 py-1 rounded font-medium"
              >
                <Gift className="w-3 h-3" /> Get Charter Discount
              </Link>
            </div>
          </div>
        )}

        {/* Mission Statement Row (separate collapsible) */}
        <div className={`flex items-center justify-between gap-3 ${betaExpanded || missionExpanded ? 'mt-3 pt-2 border-t border-amber-200' : 'mt-3'}`}>
          <div className="flex items-center gap-2 min-w-0">
            <Target className="w-4 h-4 text-amber-700 shrink-0" />
            <p className="text-sm text-amber-900">
              <span className="font-semibold">Our Purpose</span>
              <span className="hidden sm:inline text-amber-800"> — Building a real-world community of dog people, run by members</span>
            </p>
          </div>
          <button
            onClick={() => setMissionExpanded(!missionExpanded)}
            className="flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 font-medium transition-colors shrink-0"
            data-testid="mission-toggle"
          >
            {missionExpanded ? 'Less' : 'Read More'}
            {missionExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>

        {/* Mission Details (collapsible) */}
        {missionExpanded && (
          <div className="mt-2 p-3 bg-amber-100/50 border border-amber-200 rounded-lg" data-testid="mission-details">
            <p className="text-xs text-amber-900 leading-relaxed">
              <span className="font-semibold">Our Purpose:</span> To enable a new real-world community of 
              "dog people" — run by club members and working collaboratively with other kennel clubs, 
              dog-related organizations, industry participants, and even governments for the benefit of all.
            </p>
            <p className="text-xs text-amber-900 leading-relaxed mt-2">
              When you join as a free or paid member, you will have a voice in how the club is run. 
              We can help fund NGOs, lobby for local dog parks, assist in research, and find local activities.
            </p>
            <div className="mt-2 flex gap-3">
              <Link
                to="/about"
                className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium hover:text-amber-900"
              >
                Learn more about us →
              </Link>
              <Link
                to="/about/bylaws"
                className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium hover:text-amber-900"
              >
                Read our bylaws →
              </Link>
            </div>
          </div>
        )}

        {/* This is Your Club Row (separate collapsible) */}
        <div className={`flex items-center justify-between gap-3 ${missionExpanded || ownershipExpanded ? 'mt-3 pt-2 border-t border-amber-200' : 'mt-3'}`}>
          <div className="flex items-center gap-2 min-w-0">
            <Users className="w-4 h-4 text-amber-700 shrink-0" />
            <p className="text-sm text-amber-900">
              <span className="font-semibold">This is Your Club!</span>
              <span className="hidden sm:inline text-amber-800"> — Members shape governance, programs, and the future</span>
            </p>
          </div>
          <button
            onClick={() => setOwnershipExpanded(!ownershipExpanded)}
            className="flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 font-medium transition-colors shrink-0"
            data-testid="ownership-toggle"
          >
            {ownershipExpanded ? 'Less' : 'Read More'}
            {ownershipExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>

        {/* Ownership Details (collapsible) */}
        {ownershipExpanded && (
          <div className="mt-2 p-3 bg-amber-100/50 border border-amber-200 rounded-lg" data-testid="ownership-details">
            <p className="text-xs text-amber-900 leading-relaxed">
              <span className="font-semibold">Member-Driven Governance:</span> Unlike traditional social platforms 
              where decisions are made in corporate boardrooms, the Club puts members first. Your voice matters in 
              elections, policy discussions, and community direction.
            </p>
            <p className="text-xs text-amber-900 leading-relaxed mt-2">
              <span className="font-semibold">What members influence:</span> Club programs and events, 
              partnerships with rescues and NGOs, community guidelines, feature priorities, and how we 
              allocate resources to benefit dogs and their people.
            </p>
            <p className="text-xs text-amber-900 leading-relaxed mt-2">
              <span className="font-semibold">Transparency:</span> We publish financial reports, hold open 
              elections for club officers, and welcome feedback through our Founding Member preview. This is a community 
              built together, not handed down from above.
            </p>
            <div className="mt-2 flex gap-3">
              <Link
                to="/elections"
                className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium hover:text-amber-900"
              >
                View elections →
              </Link>
              <Link
                to="/beta-feedback"
                className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium hover:text-amber-900"
              >
                Share feedback →
              </Link>
            </div>
          </div>
        )}

        {/* Create Your Own Community Row (separate collapsible) */}
        <div className={`flex items-center justify-between gap-3 ${ownershipExpanded || communitiesExpanded ? 'mt-3 pt-2 border-t border-amber-200' : 'mt-3'}`}>
          <div className="flex items-center gap-2 min-w-0">
            <MessagesSquare className="w-4 h-4 text-amber-700 shrink-0" />
            <p className="text-sm text-amber-900">
              <span className="font-semibold">Create Your Own Community</span>
              <span className="hidden sm:inline text-amber-800"> — Start a sub-club around any breed, topic, or location</span>
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/communities/create"
              className="hidden sm:inline-flex items-center gap-1 text-xs bg-amber-600 hover:bg-amber-700 text-white px-2 py-1 rounded font-medium transition-colors"
              data-testid="banner-create-community-cta"
            >
              + New
            </Link>
            <button
              onClick={() => setCommunitiesExpanded(!communitiesExpanded)}
              className="flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 font-medium transition-colors"
              data-testid="communities-toggle"
            >
              {communitiesExpanded ? 'Less' : 'Read More'}
              {communitiesExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </div>
        </div>

        {/* Communities Details (collapsible) */}
        {communitiesExpanded && (
          <div className="mt-2 p-3 bg-amber-100/50 border border-amber-200 rounded-lg" data-testid="communities-details">
            <p className="text-xs text-amber-900 leading-relaxed">
              <span className="font-semibold">Communities are member-run sub-clubs</span> within the Club — think
              breed groups (Golden Retriever Lovers), training circles (Trick Training Tips), or local
              meet-up hubs (NYC Dog Owners). Anyone can start one in under a minute and run it however they like.
            </p>
            <p className="text-xs text-amber-900 leading-relaxed mt-2">
              <span className="font-semibold">You're in charge:</span> set the rules, approve members,
              pin posts, and grow the conversation. Your community lives inside the Club's social fabric, so
              members don't need to juggle yet another app.
            </p>
            <div className="mt-2 flex gap-3">
              <Link
                to="/communities/create"
                className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium hover:text-amber-900"
                data-testid="banner-create-community-link"
              >
                Start one now →
              </Link>
              <Link
                to="/communities"
                className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium hover:text-amber-900"
                data-testid="banner-browse-communities-link"
              >
                Browse existing communities →
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
